import React, { useMemo, useState } from "react";
import ReactDOMServer from "react-dom/server";
import dayjs from "dayjs";
import { Modal, Button, Form } from "react-bootstrap";
import { Printer } from "react-feather";
import AttendanceDayPrintPreview from "./AttendanceDayPrintPreview";
import {
  STATUS,
  classifyRows,
  loadReportingSettings,
  saveReportingSettings,
  summarize,
} from "./attendanceDayReport";

const SHOW = [
  { value: "all", label: "All posts" },
  { value: "exceptions", label: "Late + not marked" },
  { value: "late", label: "Late only" },
  { value: "notMarked", label: "Not marked only" },
  { value: "marked", label: "Marked only" },
];

const matchesShow = (row, show) => {
  switch (show) {
    case "exceptions":
      return row.status !== STATUS.ON_TIME;
    case "late":
      return row.status === STATUS.LATE;
    case "notMarked":
      return row.status === STATUS.NOT_MARKED;
    case "marked":
      return row.isMarked;
    default:
      return true;
  }
};

/**
 * Print for one day's attendance, as filtered on the dashboard. Same popup
 * pattern as the Duty List print: static markup into a blank window, the
 * browser's dialog prints or saves as PDF, table headers repeat per page.
 *
 * The reporting time decides who counts as late. It starts at the shift
 * changeover and is remembered per shift in this browser - office staff are
 * typically checked against 08:00, guards against the shift start.
 */
const AttendanceDayPrintModal = ({ show, onClose, rows, attendanceDate, shift, filterText, branchCode, companyName }) => {
  const [reporting, setReporting] = useState(() => loadReportingSettings(shift));
  const [showWhat, setShowWhat] = useState("all");
  const [shadeExceptions, setShadeExceptions] = useState(true);

  const updateReporting = (patch) =>
    setReporting((prev) => {
      const next = { ...prev, ...patch };
      saveReportingSettings(shift, next);
      return next;
    });

  const classified = useMemo(() => classifyRows(rows, attendanceDate, reporting), [rows, attendanceDate, reporting]);
  // The counts always cover every post in the filters; "show" only trims the table.
  const summary = useMemo(() => summarize(classified), [classified]);
  const printed = classified.filter((r) => matchesShow(r, showWhat));

  const dateText = dayjs(attendanceDate).format("dddd, D MMMM YYYY");
  const title = "Attendance Report";
  const previewProps = {
    rows: printed,
    summary,
    title,
    filterText: `${dateText} · ${shift} shift${filterText ? ` · ${filterText}` : ""}`,
    reportingText:
      `Reporting time ${dayjs(`2000-01-01T${reporting.time}`).format("h:mm A")}` +
      (Number(reporting.graceMinutes) > 0 ? ` + ${reporting.graceMinutes} min grace` : "") +
      ` · Showing: ${SHOW.find((s) => s.value === showWhat).label}`,
    branchCode,
    companyName,
    shadeExceptions,
  };

  const handlePrint = () => {
    const bodyHtml = ReactDOMServer.renderToStaticMarkup(<AttendanceDayPrintPreview {...previewProps} />);
    const win = window.open("", "_blank", "width=1000,height=900");
    if (!win) return;
    win.document.write(
      `<html><head><title>${title} ${dayjs(attendanceDate).format("YYYY-MM-DD")} ${shift}</title>` +
        `<style>@page { size: A4 portrait; margin: 8mm; } html,body { margin:0; padding:0; }` +
        ` thead { display: table-header-group; } tr { page-break-inside: avoid; }` +
        ` * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }</style>` +
        `</head><body onload="window.print();window.close();">${bodyHtml}</body></html>`
    );
    win.document.close();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered scrollable fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {title}
          <span className="text-muted ms-2 fs-6">print preview</span>
        </Modal.Title>
      </Modal.Header>
      <div className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 border-bottom small">
        <div className="d-flex align-items-center gap-2">
          <span className="text-nowrap">Reporting time</span>
          <Form.Control
            type="time"
            size="sm"
            style={{ width: 120 }}
            value={reporting.time}
            onChange={(e) => e.target.value && updateReporting({ time: e.target.value })}
          />
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-nowrap">Grace (min)</span>
          <Form.Control
            type="number"
            size="sm"
            min={0}
            max={240}
            style={{ width: 80 }}
            value={reporting.graceMinutes}
            onChange={(e) => updateReporting({ graceMinutes: Math.max(0, Number(e.target.value) || 0) })}
          />
        </div>
        <Form.Select size="sm" style={{ width: 180 }} value={showWhat} onChange={(e) => setShowWhat(e.target.value)}>
          {SHOW.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Form.Select>
        <Form.Check
          type="switch"
          id="att-shade"
          label="Shade late / not marked"
          checked={shadeExceptions}
          onChange={() => setShadeExceptions((v) => !v)}
        />
        <span className="text-muted ms-auto">
          {summary.late} late · {summary.notMarked} not marked of {summary.total}
        </span>
      </div>
      <Modal.Body style={{ background: "#e9ecef" }}>
        <div style={{ background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,.2)", width: "210mm", maxWidth: "100%", margin: "0 auto", overflowX: "auto" }}>
          <AttendanceDayPrintPreview {...previewProps} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handlePrint} className="d-inline-flex align-items-center gap-2">
          <Printer size={16} /> Print
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AttendanceDayPrintModal;
